import React, { useState, useEffect, useRef } from 'react';
import { MapPin, X, Compass, AlertCircle } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { getGoogleMapsLoader } from '../utils/googleMapsLoader';

interface LocationSearchProps {
  onLocationSelect: (location: { name: string; latitude: number; longitude: number }) => void;
  placeholder?: string;
  initialLocation?: string;
  showCurrentLocation?: boolean;
}

const LocationSearch: React.FC<LocationSearchProps> = ({
  onLocationSelect,
  placeholder = 'Search for a location...',
  initialLocation,
  showCurrentLocation = true,
}) => {
  const [searchTerm, setSearchTerm] = useState(initialLocation || '');
  const [predictions, setPredictions] = useState<google.maps.places.AutocompletePrediction[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [showResults, setShowResults] = useState(false);
  const [isGettingLocation, setIsGettingLocation] = useState(false);
  const [locationError, setLocationError] = useState('');

  const autocompleteService = useRef<google.maps.places.AutocompleteService | null>(null);
  const placesService = useRef<google.maps.places.PlacesService | null>(null);
  const geocoder = useRef<google.maps.Geocoder | null>(null);
  const suppressNextFetch = useRef(false); // 👈 NEW: prevents refetch after selection
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Initialize Google Maps services
  useEffect(() => {
    getGoogleMapsLoader()
      .then(() => {
        autocompleteService.current = new google.maps.places.AutocompleteService();
        geocoder.current = new google.maps.Geocoder();
        const dummyDiv = document.createElement('div');
        placesService.current = new google.maps.places.PlacesService(dummyDiv);
      })
      .catch((err) => {
        console.error('Failed to load Google Maps API:', err);
        const errorMessage = err instanceof Error ? err.message : 'Failed to load location search. Please try again later.';
        setError(errorMessage);
      });
  }, []);

  // Debounced search
  useEffect(() => {
    // Skip one cycle after we programmatically set the input on selection
    if (suppressNextFetch.current) {
      suppressNextFetch.current = false;
      return;
    }

    const q = searchTerm.trim();

    if (!q || q.length < 2 || !autocompleteService.current) {
      setPredictions([]);
      setShowResults(false);
      return;
    }

    const debounceTimer = setTimeout(() => {
      setIsLoading(true);
      setError('');
      const searchOptions: google.maps.places.AutocompletionRequest = {
        input: q,
        componentRestrictions: { country: 'gb' },
        types: ['geocode', 'establishment'],
      };

      autocompleteService.current?.getPlacePredictions(searchOptions, (results, status) => {
        if (status === google.maps.places.PlacesServiceStatus.OK && results) {
          setPredictions(results);
          setShowResults(true);
        } else {
          setPredictions([]);
          setShowResults(false);
          if (status === google.maps.places.PlacesServiceStatus.ZERO_RESULTS) {
            setError('No locations found');
          }
        }
        setIsLoading(false);
      });
    }, 250);

    return () => clearTimeout(debounceTimer);
  }, [searchTerm]);

  const selectPrediction = (prediction: google.maps.places.AutocompletePrediction) => {
    if (!placesService.current) return;

    placesService.current.getDetails(
      { placeId: prediction.place_id, fields: ['formatted_address', 'geometry', 'name'] },
      (place, status) => {
        if (status === google.maps.places.PlacesServiceStatus.OK && place && place.geometry?.location) {
          const location = {
            name: place.formatted_address || prediction.description,
            latitude: place.geometry.location.lat(),
            longitude: place.geometry.location.lng(),
          };

          // Prevent the search effect from refetching for the new value
          suppressNextFetch.current = true;
          setPredictions([]);
          setShowResults(false);
          setSearchTerm(location.name);
          onLocationSelect(location);
        }
      }
    );
  };

  const handleUseCurrentLocation = () => {
    setIsGettingLocation(true);
    setLocationError('');

    if (!navigator.geolocation) {
      setLocationError('Geolocation is not supported by your browser');
      setIsGettingLocation(false);
      return;
    }

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;

        if (!geocoder.current) {
          setLocationError('Location service unavailable');
          setIsGettingLocation(false);
          return;
        }

        try {
          const result = await geocoder.current.geocode({ location: { lat: latitude, lng: longitude } });
          const name = result.results[0]?.formatted_address || 'Current location';

          suppressNextFetch.current = true;
          setPredictions([]);
          setShowResults(false);
          setSearchTerm(name);
          onLocationSelect({ name, latitude, longitude });
        } catch {
          setLocationError('Failed to get address for your location');
        } finally {
          setIsGettingLocation(false);
        }
      },
      (error) => {
        setIsGettingLocation(false);
        switch (error.code) {
          case error.PERMISSION_DENIED:
            setLocationError('Please enable location access to use this feature');
            break;
          case error.POSITION_UNAVAILABLE:
            setLocationError('Location information is unavailable');
            break;
          case error.TIMEOUT:
            setLocationError('Location request timed out');
            break;
          default:
            setLocationError('An error occurred getting your location');
        }
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );
  };

  const clearSearch = () => {
    setSearchTerm('');
    setPredictions([]);
    setShowResults(false);
    setLocationError('');
  };

  return (
    <div className="relative">
      <div className="relative">
        <input
          ref={searchInputRef}
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder={placeholder}
          className="pr-10 p-2 sm:p-3 w-full bg-primary-100 border border-primary-200 rounded-md focus:ring-2 focus:ring-secondary focus:border-secondary text-contrast placeholder-contrast-500 min-h-[44px]"
          autoComplete="new-password"
          name="vybe-location"
          autoCorrect="off"
          autoCapitalize="none"
          spellCheck={false}
          onKeyDown={(e) => {
            if (e.key === 'Enter') e.preventDefault(); // avoid form submit
          }}
          onFocus={() => {
            if (predictions.length > 0) setShowResults(true);
          }}
          onBlur={() => {
            setTimeout(() => setShowResults(false), 160); // allow selection to fire first
          }}
        />
        {searchTerm && (
          <button
            type="button"
            onClick={clearSearch}
            className="absolute inset-y-0 right-0 pr-3 flex items-center text-contrast-400 hover:text-contrast-600 min-h-[44px] min-w-[44px] justify-center"
          >
            <X size={18} />
          </button>
        )}
      </div>

      {showCurrentLocation && (
        <button
          type="button"
          onClick={handleUseCurrentLocation}
          disabled={isGettingLocation}
          className="mt-2 flex items-center text-sm text-secondary hover:text-secondary-600 disabled:opacity-50 min-h-[32px]"
        >
          <Compass size={16} className="mr-1" />
          {isGettingLocation ? 'Getting location...' : 'Use my current location'}
        </button>
      )}

      {locationError && (
        <p className="mt-2 text-sm text-secondary flex items-center">
          <AlertCircle size={14} className="mr-1" />
          {locationError}
        </p>
      )}

      {isLoading && (
        <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
          <div className="animate-spin h-4 w-4 border-2 border-secondary rounded-full border-t-transparent"></div>
        </div>
      )}

      <AnimatePresence>
        {showResults && predictions.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="absolute z-20 mt-1 w-full bg-primary-50 rounded-md shadow-elegant max-h-48 sm:max-h-60 overflow-y-auto border border-primary-200"
          >
            <ul className="py-1">
              {predictions.map((prediction, index) => (
                <motion.li
                  key={prediction.place_id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="px-3 sm:px-4 py-2 hover:bg-primary-100 cursor-pointer text-contrast min-h-[44px] flex items-center"
                  // Use mousedown so it runs before input blur → no double click
                  onMouseDown={(e) => {
                    e.preventDefault();
                    selectPrediction(prediction);
                  }}
                >
                  <div className="flex items-center">
                    <MapPin size={16} className="text-secondary mr-2 flex-shrink-0" />
                    <div>
                      <div className="font-medium text-sm">{prediction.structured_formatting.main_text}</div>
                      <div className="text-xs text-contrast-500 line-clamp-1">
                        {prediction.structured_formatting.secondary_text}
                      </div>
                    </div>
                  </div>
                </motion.li>
              ))}
            </ul>
          </motion.div>
        )}
      </AnimatePresence>

      {error && <p className="text-secondary text-sm mt-1">{error}</p>}
    </div>
  );
};

export default LocationSearch;
